import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  Building2, Users, FolderKanban, Plus, Edit, Trash2, ArrowLeft,
  Loader2, UserPlus, Globe, Phone, Mail, CheckCircle, XCircle, RefreshCw,
  FileStack, Clock
} from "lucide-react";

const STATUS_BADGE = {
  active: "bg-emerald-100 text-emerald-700",
  paused: "bg-amber-100 text-amber-700",
  closed: "bg-red-100 text-red-700",
  completed: "bg-blue-100 text-blue-700",
  on_hold: "bg-amber-100 text-amber-700"
};

const PROJECT_TYPES = ["Consulting", "Marketing", "Website", "Branding", "SEO", "Business Setup", "SOP Creation", "Admin Support", "Media Promo", "Other"];

const emptyCompanyForm = {
  name: "", industry: "", website: "", primary_contact_name: "",
  primary_contact_email: "", primary_contact_phone: "",
  billing_info: "", contract_notes: "", status: "active"
};
const emptyEmployeeForm = { name: "", email: "", job_title: "", phone: "", password: "Welcome2026!" };
const emptyProjectForm = { title: "", project_type: "Consulting", description: "", goals: "", assigned_employees: [], status: "active", milestones: [], notes: "" };

export const CompanyManagementPanel = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetail, setCompanyDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Dialog visibility
  const [dialog, setDialog] = useState(null); // 'createCompany' | 'editCompany' | 'addEmployee' | 'addProject' | 'editProject'
  const [editingProject, setEditingProject] = useState(null);
  const [templates, setTemplates] = useState([]);

  // Forms
  const [companyForm, setCompanyForm] = useState(emptyCompanyForm);
  const [employeeForm, setEmployeeForm] = useState(emptyEmployeeForm);
  const [projectForm, setProjectForm] = useState(emptyProjectForm);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/consultant/companies`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchCompanyDetail = async (companyId) => {
    setDetailLoading(true);
    try {
      const res = await authFetch(`${API}/consultant/companies/${companyId}`);
      if (res.ok) setCompanyDetail(await res.json());
    } catch (e) { console.error(e); }
    setDetailLoading(false);
  };

  useEffect(() => { if (open) fetchCompanies(); }, [open]);

  const fetchTemplates = async () => {
    try {
      const res = await authFetch(`${API}/consultant/project-templates`);
      if (res.ok) { const d = await res.json(); setTemplates(d.templates || []); }
    } catch (e) { console.error(e); }
  };

  const applyTemplate = (tmpl) => {
    setProjectForm({
      title: tmpl.name || "",
      project_type: tmpl.project_type || "Consulting",
      description: tmpl.description || "",
      goals: tmpl.goals || "",
      assigned_employees: [],
      status: "active",
      milestones: tmpl.milestones || [],
      notes: tmpl.notes || ""
    });
    toast.success(`Template "${tmpl.name}" applied`);
  };

  const closeDialog = () => {
    setDialog(null);
    setEditingProject(null);
  };

  // ========== COMPANY CRUD ==========
  const handleCreateCompany = async () => {
    if (!companyForm.name) { toast.error("Company name required"); return; }
    setSaving(true);
    try {
      const res = await authFetch(`${API}/consultant/companies`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(companyForm)
      });
      if (res.ok) { toast.success("Company created"); closeDialog(); setCompanyForm(emptyCompanyForm); fetchCompanies(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || `Failed (${res.status})`); }
    } catch (e) { toast.error(`Network error: ${e.message || "Could not connect"}`); }
    setSaving(false);
  };

  const handleUpdateCompany = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/consultant/companies/${selectedCompany}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(companyForm)
      });
      if (res.ok) { toast.success("Updated"); closeDialog(); fetchCompanies(); fetchCompanyDetail(selectedCompany); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Update failed"); }
    } catch (e) { toast.error(`Error: ${e.message}`); }
    setSaving(false);
  };

  const handleDeleteCompany = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Employees and projects will be unlinked.`)) return;
    try {
      const res = await authFetch(`${API}/consultant/companies/${id}`, { method: "DELETE" });
      if (res.ok) { toast.success("Deleted"); setSelectedCompany(null); setCompanyDetail(null); fetchCompanies(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Delete failed"); }
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  // ========== EMPLOYEE CRUD ==========
  const handleAddEmployee = async () => {
    if (!employeeForm.name || !employeeForm.email) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      const res = await authFetch(`${API}/consultant/companies/${selectedCompany}/employees`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(employeeForm)
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`${employeeForm.name} added (password: ${d.password})`);
        closeDialog(); setEmployeeForm(emptyEmployeeForm); fetchCompanyDetail(selectedCompany);
      } else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Failed to add employee"); }
    } catch (e) { toast.error(`Error: ${e.message}`); }
    setSaving(false);
  };

  const handleRemoveEmployee = async (empId, name) => {
    if (!window.confirm(`Remove ${name} from this company?`)) return;
    try {
      const res = await authFetch(`${API}/consultant/companies/${selectedCompany}/employees/${empId}`, { method: "DELETE" });
      if (res.ok) { toast.success("Removed"); fetchCompanyDetail(selectedCompany); }
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  // ========== PROJECT CRUD ==========
  const handleCreateProject = async () => {
    if (!projectForm.title) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const res = await authFetch(`${API}/consultant/companies/${selectedCompany}/projects`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(projectForm)
      });
      if (res.ok) {
        toast.success("Project created");
        closeDialog();
        setProjectForm(emptyProjectForm);
        fetchCompanyDetail(selectedCompany);
        fetchCompanies();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || `Failed to create project (${res.status})`);
      }
    } catch (e) { toast.error(`Network error: ${e.message || "Could not connect"}`); }
    setSaving(false);
  };

  const handleUpdateProject = async (projectId, updates) => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/consultant/companies/${selectedCompany}/projects/${projectId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        toast.success("Project updated");
        if (dialog === "editProject") closeDialog();
        fetchCompanyDetail(selectedCompany);
        fetchCompanies();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Update failed");
      }
    } catch (e) { toast.error(`Error: ${e.message}`); }
    setSaving(false);
  };

  const handleEditProjectSubmit = async () => {
    if (!projectForm.title) { toast.error("Title required"); return; }
    await handleUpdateProject(editingProject.project_id, projectForm);
  };

  const openEditProject = (proj) => {
    setEditingProject(proj);
    setProjectForm({
      title: proj.title || "",
      project_type: proj.project_type || "Consulting",
      description: proj.description || "",
      goals: proj.goals || "",
      assigned_employees: proj.assigned_employees || [],
      status: proj.status || "active"
    });
    setDialog("editProject");
  };

  if (!open) return null;
  const stats = data?.stats || {};
  const detail = companyDetail;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="company-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {selectedCompany && detail ? (
              <><Button variant="ghost" size="sm" onClick={() => { setSelectedCompany(null); setCompanyDetail(null); }}><ArrowLeft className="w-4 h-4" /></Button>{detail.company.name}</>
            ) : (
              <><Building2 className="w-5 h-5 text-blue-600" />Company Management</>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">Manage companies, employees, and projects</DialogDescription>
        </DialogHeader>

        {loading && !selectedCompany ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : selectedCompany && detail ? (
          /* ========== COMPANY DETAIL VIEW ========== */
          <div className="flex-1 overflow-y-auto space-y-4">
            {detailLoading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
              <>
              {/* Company Info */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold">{detail.company.name}</h3>
                        <Badge className={STATUS_BADGE[detail.company.status]}>{detail.company.status}</Badge>
                      </div>
                      {detail.company.industry && <p className="text-sm text-neutral-500">{detail.company.industry}</p>}
                      <div className="flex gap-4 text-xs text-neutral-500 mt-2">
                        {detail.company.website && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{detail.company.website}</span>}
                        {detail.company.primary_contact_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{detail.company.primary_contact_email}</span>}
                        {detail.company.primary_contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{detail.company.primary_contact_phone}</span>}
                      </div>
                      {detail.company.contract_notes && <p className="text-xs text-neutral-400 mt-2 italic">{detail.company.contract_notes}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" data-testid="edit-company-btn" onClick={() => { setCompanyForm({...detail.company}); setDialog("editCompany"); }}><Edit className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteCompany(selectedCompany, detail.company.name)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Employees */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-blue-600" />Employees ({detail.employees.length})</CardTitle>
                    <Button size="sm" data-testid="add-employee-btn" onClick={() => { setEmployeeForm(emptyEmployeeForm); setDialog("addEmployee"); }}><UserPlus className="w-3 h-3 mr-1" />Add Employee</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {detail.employees.length > 0 ? (
                    <div className="space-y-2">
                      {detail.employees.map(emp => (
                        <div key={emp.user_id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center"><Users className="w-4 h-4 text-blue-600" /></div>
                            <div>
                              <p className="text-sm font-medium">{emp.name}</p>
                              <p className="text-xs text-neutral-500">{emp.job_title || emp.email}</p>
                            </div>
                          </div>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleRemoveEmployee(emp.user_id, emp.name)}><XCircle className="w-4 h-4" /></Button>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-sm text-neutral-400 text-center py-4">No employees yet</p>}
                </CardContent>
              </Card>

              {/* Projects */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2"><FolderKanban className="w-4 h-4 text-purple-600" />Projects ({detail.projects.length})</CardTitle>
                    <Button size="sm" data-testid="add-project-btn" onClick={() => { setProjectForm(emptyProjectForm); fetchTemplates(); setDialog("addProject"); }}><Plus className="w-3 h-3 mr-1" />New Project</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {detail.projects.length > 0 ? (
                    <div className="space-y-3">
                      {detail.projects.map(proj => (
                        <div key={proj.project_id} className="p-4 bg-neutral-50 rounded-lg border" data-testid={`project-row-${proj.project_id}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-sm">{proj.title}</h4>
                                <Badge className={STATUS_BADGE[proj.status] || ""} variant="outline">{proj.status}</Badge>
                                <Badge variant="outline" className="text-xs">{proj.project_type}</Badge>
                              </div>
                              {proj.description && <p className="text-xs text-neutral-500 mt-1">{proj.description}</p>}
                              {proj.goals && <p className="text-xs text-neutral-400 mt-0.5">Goals: {proj.goals}</p>}
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Edit project" data-testid={`edit-project-${proj.project_id}`} onClick={() => openEditProject(proj)}>
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Select value={proj.status} onValueChange={v => handleUpdateProject(proj.project_id, { status: v })}>
                                <SelectTrigger className="w-28 h-7 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="active">Active</SelectItem>
                                  <SelectItem value="on_hold">On Hold</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <Progress value={proj.percentage_complete || 0} className="h-1.5 mb-2" />
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-xs text-neutral-500">
                              <span>{proj.percentage_complete || 0}% complete</span>
                              {proj.assigned_names?.length > 0 && <span>| {proj.assigned_names.join(", ")}</span>}
                            </div>
                            <div className="flex gap-1">
                              <Input type="number" min="0" max="100" className="w-16 h-7 text-xs" defaultValue={proj.percentage_complete || 0}
                                onBlur={e => { const v = parseInt(e.target.value); if (!isNaN(v)) handleUpdateProject(proj.project_id, { percentage_complete: v }); }} />
                              <span className="text-xs text-neutral-400 self-center">%</span>
                            </div>
                          </div>
                          {proj.milestones?.length > 0 && (
                            <div className="mt-2 pt-2 border-t space-y-1">
                              {proj.milestones.map((m, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                  <CheckCircle className={`w-3 h-3 ${m.done ? "text-emerald-500" : "text-neutral-300"}`} />
                                  <span className={m.done ? "line-through text-neutral-400" : "text-neutral-600"}>{m.title}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-sm text-neutral-400 text-center py-4">No projects yet</p>}
                </CardContent>
              </Card>
              </>
            )}
          </div>
        ) : (
          /* ========== COMPANY LIST VIEW ========== */
          <div className="flex-1 overflow-y-auto space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex gap-4 text-sm">
                <span>Total: <strong>{stats.total || 0}</strong></span>
                <span className="text-emerald-600">Active: <strong>{stats.active || 0}</strong></span>
                <span>Employees: <strong>{stats.total_employees || 0}</strong></span>
                <span>Projects: <strong>{stats.total_projects || 0}</strong></span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" data-testid="create-company-btn" onClick={() => { setCompanyForm(emptyCompanyForm); setDialog("createCompany"); }}><Plus className="w-4 h-4 mr-1" />Add Company</Button>
                <Button size="sm" variant="ghost" onClick={fetchCompanies}><RefreshCw className="w-4 h-4" /></Button>
              </div>
            </div>

            {(data?.companies || []).length > 0 ? (data.companies).map(company => (
              <Card key={company.company_id} className="hover:shadow-sm transition-shadow cursor-pointer" onClick={() => { setSelectedCompany(company.company_id); fetchCompanyDetail(company.company_id); }}>
                <CardContent className="py-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="font-semibold">{company.name}</h3>
                      <Badge className={STATUS_BADGE[company.status]} variant="outline">{company.status}</Badge>
                    </div>
                    <p className="text-xs text-neutral-500">{company.industry || "No industry set"}</p>
                  </div>
                  <div className="flex gap-4 text-center flex-shrink-0">
                    <div><p className="text-lg font-bold text-blue-600">{company.employee_count}</p><p className="text-[10px] text-neutral-500">Employees</p></div>
                    <div><p className="text-lg font-bold text-purple-600">{company.project_count}</p><p className="text-[10px] text-neutral-500">Projects</p></div>
                    <div><p className="text-lg font-bold text-emerald-600">{company.active_projects}</p><p className="text-[10px] text-neutral-500">Active</p></div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <div className="text-center py-12 text-neutral-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="mb-4">No companies yet</p>
                <Button data-testid="create-first-company-btn" onClick={() => { setCompanyForm(emptyCompanyForm); setDialog("createCompany"); }}><Plus className="w-4 h-4 mr-1" />Add First Company</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Create/Edit Company Dialog */}
    <Dialog open={dialog === "createCompany" || dialog === "editCompany"} onOpenChange={o => { if (!o) closeDialog(); }}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialog === "editCompany" ? "Edit Company" : "Add Company"}</DialogTitle>
          <DialogDescription>{dialog === "editCompany" ? "Update company information" : "Create a new company"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div><Label>Company Name *</Label><Input data-testid="company-name-input" value={companyForm.name} onChange={e => setCompanyForm({...companyForm, name: e.target.value})} placeholder="Acme Corp" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Industry</Label><Input value={companyForm.industry} onChange={e => setCompanyForm({...companyForm, industry: e.target.value})} placeholder="Technology" /></div>
            <div><Label>Website</Label><Input value={companyForm.website} onChange={e => setCompanyForm({...companyForm, website: e.target.value})} placeholder="https://..." /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Primary Contact</Label><Input value={companyForm.primary_contact_name} onChange={e => setCompanyForm({...companyForm, primary_contact_name: e.target.value})} placeholder="Name" /></div>
            <div><Label>Contact Email</Label><Input value={companyForm.primary_contact_email} onChange={e => setCompanyForm({...companyForm, primary_contact_email: e.target.value})} placeholder="email@company.com" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contact Phone</Label><Input value={companyForm.primary_contact_phone} onChange={e => setCompanyForm({...companyForm, primary_contact_phone: e.target.value})} placeholder="(555) 123-4567" /></div>
            <div><Label>Status</Label>
              <Select value={companyForm.status} onValueChange={v => setCompanyForm({...companyForm, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="paused">Paused</SelectItem><SelectItem value="closed">Closed</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Billing Info</Label><Input value={companyForm.billing_info} onChange={e => setCompanyForm({...companyForm, billing_info: e.target.value})} placeholder="Billing details" /></div>
          <div><Label>Contract Notes</Label><Textarea value={companyForm.contract_notes} onChange={e => setCompanyForm({...companyForm, contract_notes: e.target.value})} placeholder="Contract terms, renewal dates..." rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={closeDialog}>Cancel</Button>
          <Button data-testid="save-company-btn" onClick={dialog === "editCompany" ? handleUpdateCompany : handleCreateCompany} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{dialog === "editCompany" ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Add Employee Dialog */}
    <Dialog open={dialog === "addEmployee"} onOpenChange={o => { if (!o) closeDialog(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Employee</DialogTitle>
          <DialogDescription>Create a new account for this company</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name *</Label><Input data-testid="employee-name-input" value={employeeForm.name} onChange={e => setEmployeeForm({...employeeForm, name: e.target.value})} /></div>
            <div><Label>Email *</Label><Input data-testid="employee-email-input" value={employeeForm.email} onChange={e => setEmployeeForm({...employeeForm, email: e.target.value})} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Job Title</Label><Input value={employeeForm.job_title} onChange={e => setEmployeeForm({...employeeForm, job_title: e.target.value})} /></div>
            <div><Label>Phone</Label><Input value={employeeForm.phone} onChange={e => setEmployeeForm({...employeeForm, phone: e.target.value})} /></div>
          </div>
          <div><Label>Temp Password</Label><Input value={employeeForm.password} onChange={e => setEmployeeForm({...employeeForm, password: e.target.value})} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={closeDialog}>Cancel</Button>
          <Button data-testid="save-employee-btn" onClick={handleAddEmployee} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}Add Employee</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Create / Edit Project Dialog */}
    <Dialog open={dialog === "addProject" || dialog === "editProject"} onOpenChange={o => { if (!o) closeDialog(); }}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialog === "editProject" ? "Edit Project" : "New Project"}</DialogTitle>
          <DialogDescription>
            {dialog === "editProject" ? `Editing: ${editingProject?.title}` : `Create a project for ${detail?.company?.name || "company"}`}
          </DialogDescription>
        </DialogHeader>

        {/* Template Picker — only for new projects */}
        {dialog === "addProject" && templates.length > 0 && (
          <div className="mb-1">
            <Label className="text-xs text-neutral-500 mb-1.5 block">Quick Start from Template</Label>
            <div className="flex flex-wrap gap-1.5">
              {templates.map(t => (
                <button
                  key={t.template_id}
                  type="button"
                  onClick={() => applyTemplate(t)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-neutral-200 hover:border-emerald-400 hover:bg-emerald-50 transition-colors text-xs font-medium text-neutral-700 hover:text-emerald-700"
                  data-testid={`template-${t.template_id}`}
                >
                  <FileStack className="w-3 h-3" />
                  {t.name}
                  {t.estimated_weeks > 0 && <span className="text-[10px] text-neutral-400 ml-0.5">({t.estimated_weeks}wk)</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3 pt-1">
          <div><Label>Title *</Label><Input data-testid="project-title-input" value={projectForm.title} onChange={e => setProjectForm({...projectForm, title: e.target.value})} placeholder="Website Redesign" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={projectForm.project_type} onValueChange={v => setProjectForm({...projectForm, project_type: v})}>
                <SelectTrigger data-testid="project-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={projectForm.status || "active"} onValueChange={v => setProjectForm({...projectForm, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Assign Employees</Label>
            <div className="max-h-20 overflow-y-auto border rounded-lg p-2 space-y-1">
              {(detail?.employees || []).map(emp => (
                <label key={emp.user_id} className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={projectForm.assigned_employees.includes(emp.user_id)}
                    onChange={e => {
                      const ids = e.target.checked ? [...projectForm.assigned_employees, emp.user_id] : projectForm.assigned_employees.filter(id => id !== emp.user_id);
                      setProjectForm({...projectForm, assigned_employees: ids});
                    }} />
                  {emp.name}
                </label>
              ))}
              {(!detail?.employees || detail.employees.length === 0) && <p className="text-xs text-neutral-400">No employees to assign</p>}
            </div>
          </div>
          <div><Label>Description</Label><Textarea data-testid="project-description-input" value={projectForm.description} onChange={e => setProjectForm({...projectForm, description: e.target.value})} rows={2} placeholder="Project scope..." /></div>
          <div><Label>Goals</Label><Input value={projectForm.goals} onChange={e => setProjectForm({...projectForm, goals: e.target.value})} placeholder="Key objectives" /></div>

          {/* Milestones */}
          {projectForm.milestones && projectForm.milestones.length > 0 && (
            <div>
              <Label className="flex items-center gap-1">Milestones <Badge variant="outline" className="text-[10px]">{projectForm.milestones.length}</Badge></Label>
              <div className="border rounded-lg p-2 space-y-1 max-h-28 overflow-y-auto mt-1">
                {projectForm.milestones.map((m, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <CheckCircle className="w-3 h-3 text-neutral-300 flex-shrink-0" />
                    <span className="text-neutral-600">{m.title}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-neutral-400 mt-1">Pre-filled from template. Milestones can be managed after creation.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={closeDialog}>Cancel</Button>
          <Button data-testid="save-project-btn" onClick={dialog === "editProject" ? handleEditProjectSubmit : handleCreateProject} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : dialog === "editProject" ? <Edit className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            {dialog === "editProject" ? "Save Changes" : "Create Project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};
